<template>
    <v-container fluid>

    <pr-loading v-model="loading" />
    <v-snackbar v-model="showMessage" value="true" right top multi-line :color="msgType" :timeout="2000" >
        {{ msgText }}
        <v-btn dark flat @click="showMessage = false">
            {{ $t('OK') }}
        </v-btn>
    </v-snackbar>

    <v-layout row wrap>
        <v-flex xs12 sm8>
            <h1 class="primary--text">{{ $t('Export template') }}</h1>
        </v-flex>
        <v-flex xs12 sm4 class="text-xs-right">
            <v-btn @click="doExport" color="primary" class="mr-0">
                <v-icon small class="mx-2">fa-file-excel</v-icon>
                {{ $t('Export') }}
            </v-btn>
        </v-flex>
        <v-flex xs12 class="py-2 caption">
            {{ $t('Export.help') }}
        </v-flex>
    </v-layout>
    <v-layout>
        <v-flex xs7>
            <div class="primary--text text-xs-center subheading font-weight-thin text-uppercase pa-2">{{ $t('All fields') }}</div>

            <v-expansion-panel class="sourceListPanel">
                <v-expansion-panel-content
                    v-for="(group, i) in groups"
                    :key="i"
                >
                    <div slot="header" class="caption text-truncate pa-0">
                        {{ $t('Export.template.' + group + '.group.name') }}
                    </div>

                    <draggable v-model="sourceList"
                        :options="{group: 'cols', animation: 200, sort: false, ghostClass: 'sourceGhost'}"
                        style="min-height: 10px"
                    >
                        <template v-for="item in sourceList">
                            <v-chip :key="item.order" v-if="item.group == group" class="caption">
                                <span v-html="item.text" :title="item.text"></span>
                            </v-chip>
                        </template>
                    </draggable>

                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-flex>
        <v-flex xs1 class="text-xs-center pa-2">
            <v-icon>arrow_forward</v-icon>
        </v-flex>
        <v-flex xs4>
            <div class="primary--text text-xs-center subheading font-weight-thin text-uppercase pa-2">{{ $t('Export columns') }}</div>

            <draggable
                v-model="value"
                :options="{group: 'cols', animation: 200, ghostClass: 'targetGhost'}"
                style="min-height: 10px; height: calc(100% - 3rem)"
                @sort="valueChanged"
            >
                <v-chip v-for="(item, index) in value" :key="item.order" class="caption text-truncate" style="width: 100%">
                    <v-avatar class="mr-1">
                        <v-icon color="error" @click="targetItemClose(item, index)">cancel</v-icon>
                    </v-avatar>
                    <span v-html="item.text" style="width: 100%" :title="item.text"></span>
                </v-chip>
            </draggable>

        </v-flex>
    </v-layout>
    </v-container>
</template>

<script>
import PrLoading from '@/components/Loading';
import { template } from "../resources";
import { downloadFile } from "../components/util";
import { mapGetters, mapActions } from "vuex";
import draggable from 'vuedraggable';

export default {
    components : {
        PrLoading, draggable
    },

    data : () => ({
        loading : false,
        showMessage : false,
        msgType : 'info',
        msgText : '',

        value : [],
        items : [],
        groups: [],
        sourceList : [],
        disallowed : [ 'manifest', 'source.after', 'source.until', 'target.after', 'target.until', 'declared_value.ignore' ],
    }),

    computed : {
        ...mapGetters(['exportTemplate', 'globalConfig']),
    },

    methods : {
        ...mapActions(['updateExportTemplate']),

        processSourceList() {
            this.sourceList = this.sourceList.sort((a, b) => a.order - b.order);
        },

        valueChanged() {
            this.processSourceList();
            this.updateExportTemplate(JSON.stringify(this.value.map(e => e.code)));
        },

        targetItemClose(item, index) {
            this.value.splice(index, 1);
            this.sourceList.push(item);
            this.valueChanged();
        },

        doExport () {
            if(this.value.length > 0) {
                let param = "";
                for(let i = 0; i < this.value.length; i++) {
                    param += (i>0) ? "&" : "?";
                    param += this.value[i].code;
                }
                let opts = {
                    responseType: "arraybuffer",
                    headers: {
                        "Accept-Language": this.globalConfig.lang
                    }
                };

                this.loading = true;
                this.$http.get("shipment/import.jac" + param, opts).then(res => {
                    downloadFile(res);
                }, error => {
                    this.showErrorAndRedirect(error.statusText, true);
                }).finally(() => {
                    this.loading = false;
                });
            } else {
                this.msgType = 'warning';
                this.msgText = this.$t('Please select at least one export column');
                this.showMessage = true;
            }
        },

        showErrorAndRedirect(text, signin) {
            this.msgText = text;
            this.msgType = "error";
            this.showMessage = true;
            if(signin) {
              // ignore duplicate navigation errors.
              this.$router.push({ name : "signin" }).catch(e => {});
            }
        },

        initSelection () {
            let v = [];
            try {
                v = JSON.parse(this.exportTemplate);
            } catch(e) {
                v = [];
            }

            this.sourceList = this.items.slice();
            this.value  = [];
            for(let x = 0; x < v.length; x++) {
                // let t = this.items.find(e => e.code === v[x]);
                let t = this.items.find((e, i) => {
                    if (e.code === v[x]) {
                        this.sourceList.splice(i, 1, 0);
                        return true;
                    }
                });
                if (!t)
                    continue;
                this.value[x] = {
                    code: v[x],
                    text: t.text,
                    order: t.order,
                    group: t.group
                };
            }
        }
    },

    mounted() {
        this.loading = true;
        template.meta().then(resp => {
            let vm = this;
            let v = resp.body;
            if (this.disallowed.length > 0)
                v = v.filter(e => !this.disallowed.find(d => d === e.code));

            this.items = v.map((e, index) => {
                let g = e.code.split('.')[0];
                if (g.includes('return_'))
                    g = 'return_';
                else if (g.includes('test_on_delivery') || g.includes('review_on_delivery'))
                    g = 'test_review_on_delivery';
                if (!vm.groups.includes(g))
                    vm.groups.push(g);

                return {
                    code : e.code,
                    text : vm.$t('Export.template.' + e.code),
                    order: index,
                    group: g
                };
            });
            this.initSelection();
        }).catch(resp => {
            this.showErrorAndRedirect(resp.statusText, true);
        }).finally(() => {
            this.loading = false;
        });
    }
}
</script>
<style scope>
.sourceGhost {
    opacity: .5;
    width: auto !important;
}
.targetGhost {
    opacity: .5;
    width: 100%;
}
.sourceListPanel .v-expansion-panel__header {
    padding: 6px 12px;
    min-height: auto;
}
</style>